import { useSignalR } from '../components/providers/SignalRProvider.vue'
import { createSubscriptionAdder } from './subscription-utils'
import type { SignalRReceiveEvent, SubscribeToEventOptions } from '../store'
import { ref } from 'vue'
import type {
  DatabaseAccessGrantedSignalRPayload,
  DatabaseAccessRevokedSignalRPayload,
  TenantUserCreatedSignalRPayload,
  TenantUserNameChangedSignalRPayload,
  TenantUserPhoneNumberChangedSignalRPayload,
  TenantUserDeactivatedSignalRPayload,
  TenantUserActivatedSignalRPayload,
  TenantAdminRevokedSignalRPayload,
  TenantAdminGrantedSignalRPayload,
  TenantUserEmailChangedSignalRPayload,
  TenantUserProfileImageChangedSignalRPayload,
  TenantUserDeactivationScheduledSignalRPayload,
  TenantUserReactivatedSignalRPayload,
  UserJobTitleChangedSignalRPayload
} from '@gat-ship/types/signal-r-events'
import { signalREvent } from '@gat-ship/types/signal-r-events'

export const tenantUserEvents = {
  V1: {
    TenantUserPhoneNumberChanged:
      'AccessControl.V1.TenantUserPhoneNumberChanged'
  }
} as const

export type TenantUserEventHandlers = {
  onTenantUserCreated?: (
    payload: SignalRReceiveEvent<TenantUserCreatedSignalRPayload>
  ) => void
  onTenantUserNameChanged?: (
    payload: TenantUserNameChangedSignalRPayload
  ) => void
  onTenantUserPhoneNumberChanged?: (
    payload: TenantUserPhoneNumberChangedSignalRPayload
  ) => void
  onTenantUserProfileImageChanged?: (
    payload: SignalRReceiveEvent<TenantUserProfileImageChangedSignalRPayload>
  ) => void
  onTenantUserDeactivated?: (
    payload: TenantUserDeactivatedSignalRPayload
  ) => void
  onTenantUserActivated?: (payload: TenantUserActivatedSignalRPayload) => void
  onDatabaseAccessGranted?: (
    payload: SignalRReceiveEvent<DatabaseAccessGrantedSignalRPayload>
  ) => void
  onDatabaseAccessRevoked?: (
    payload: SignalRReceiveEvent<DatabaseAccessRevokedSignalRPayload>
  ) => void
  onTenantAdminGranted?: (
    payload: SignalRReceiveEvent<TenantAdminGrantedSignalRPayload>
  ) => void
  onTenantAdminRevoked?: (
    payload: SignalRReceiveEvent<TenantAdminRevokedSignalRPayload>
  ) => void
  onTenantUserEmailChanged?: (
    payload: SignalRReceiveEvent<TenantUserEmailChangedSignalRPayload>
  ) => void
  onUserDeactivationScheduled?: (
    payload: TenantUserDeactivationScheduledSignalRPayload
  ) => void
  onUserActivated?: (payload: TenantUserReactivatedSignalRPayload) => void
  onUserJobTitleChanged?: (
    payload: SignalRReceiveEvent<UserJobTitleChangedSignalRPayload>
  ) => void
}

export function useTenantUserSignalREvents(handlers?: TenantUserEventHandlers) {
  const { subscribe } = useSignalR()

  const subscription = ref<() => void>()

  function subscribeToTenantUserCreated(
    cb: (payload: SignalRReceiveEvent<TenantUserCreatedSignalRPayload>) => void,
    options?: SubscribeToEventOptions
  ) {
    return subscribe<TenantUserCreatedSignalRPayload>(
      signalREvent.AccessControl.V1.TenantUserCreated,
      (payload) => {
        cb(payload)
      },
      options
    )
  }

  function subscribeToTenantUserNameChanged(
    cb: (tenantUser: TenantUserNameChangedSignalRPayload) => void,
    options?: SubscribeToEventOptions
  ) {
    return subscribe<TenantUserNameChangedSignalRPayload>(
      signalREvent.AccessControl.V1.TenantUserNameChanged,
      (payload) => {
        cb(payload.message)
      },
      options
    )
  }

  function subscribeToTenantUserPhoneNumberChanged(
    cb: (payload: TenantUserPhoneNumberChangedSignalRPayload) => void,
    options?: SubscribeToEventOptions
  ) {
    return subscribe<TenantUserPhoneNumberChangedSignalRPayload>(
      signalREvent.AccessControl.V1.TenantUserPhoneNumberChanged,
      (payload) => {
        cb(payload.message)
      },
      options
    )
  }

  function subscribeToUserActivated(
    cb: (payload: TenantUserReactivatedSignalRPayload) => void,
    options?: SubscribeToEventOptions
  ) {
    return subscribe<TenantUserReactivatedSignalRPayload>(
      signalREvent.AccessControl.V1.UserReactivated,
      (payload) => {
        cb(payload.message)
      },
      options
    )
  }

  function subscribeToTenantUserDeactivated(
    cb: (payload: TenantUserDeactivatedSignalRPayload) => void,
    options?: SubscribeToEventOptions
  ) {
    return subscribe<TenantUserDeactivatedSignalRPayload>(
      signalREvent.AccessControl.V1.TenantUserDeactivated,
      (payload) => {
        cb(payload.message)
      },
      options
    )
  }

  function subscribeToUserDeactivationScheduled(
    cb: (payload: TenantUserDeactivationScheduledSignalRPayload) => void,
    options?: SubscribeToEventOptions
  ) {
    return subscribe<TenantUserDeactivationScheduledSignalRPayload>(
      signalREvent.AccessControl.V1.UserDeactivationScheduled,
      (payload) => {
        cb(payload.message)
      },
      options
    )
  }

  function subscribeToTenantUserActivated(
    cb: (payload: TenantUserActivatedSignalRPayload) => void,
    options?: SubscribeToEventOptions
  ) {
    return subscribe<TenantUserActivatedSignalRPayload>(
      signalREvent.AccessControl.V1.TenantUserActivated,
      (payload) => {
        cb(payload.message)
      },
      options
    )
  }

  function subscribeToDatabaseAccessGranted(
    cb: (
      payload: SignalRReceiveEvent<DatabaseAccessGrantedSignalRPayload>
    ) => void,
    options?: SubscribeToEventOptions
  ) {
    return subscribe<DatabaseAccessGrantedSignalRPayload>(
      signalREvent.AccessControl.V1.DatabaseAccessGranted,
      cb,
      options
    )
  }

  function subscribeToDatabaseAccessRevoked(
    cb: (
      payload: SignalRReceiveEvent<DatabaseAccessRevokedSignalRPayload>
    ) => void,
    options?: SubscribeToEventOptions
  ) {
    return subscribe<DatabaseAccessRevokedSignalRPayload>(
      signalREvent.AccessControl.V1.DatabaseAccessRevoked,
      cb,
      options
    )
  }

  function subscribeToTenantAdminGranted(
    cb: (
      payload: SignalRReceiveEvent<TenantAdminGrantedSignalRPayload>
    ) => void,
    options?: SubscribeToEventOptions
  ) {
    return subscribe<TenantAdminGrantedSignalRPayload>(
      signalREvent.AccessControl.V1.TenantAdminGranted,
      cb,
      options
    )
  }

  function subscribeToTenantAdminRevoked(
    cb: (
      payload: SignalRReceiveEvent<TenantAdminRevokedSignalRPayload>
    ) => void,
    options?: SubscribeToEventOptions
  ) {
    return subscribe<TenantAdminRevokedSignalRPayload>(
      signalREvent.AccessControl.V1.TenantAdminRevoked,
      cb,
      options
    )
  }

  function subscribeToTenantUserEmailChanged(
    cb: (
      payload: SignalRReceiveEvent<TenantUserEmailChangedSignalRPayload>
    ) => void,
    options?: SubscribeToEventOptions
  ) {
    return subscribe<TenantUserEmailChangedSignalRPayload>(
      signalREvent.AccessControl.V1.TenantUserEmailChanged,
      cb,
      options
    )
  }

  function subscribeToTenantUserProfileImageChanged(
    cb: (
      payload: SignalRReceiveEvent<TenantUserProfileImageChangedSignalRPayload>
    ) => void,
    options?: SubscribeToEventOptions
  ) {
    return subscribe<TenantUserProfileImageChangedSignalRPayload>(
      signalREvent.AccessControl.V1.TenantUserProfileImageChanged,
      cb,
      options
    )
  }

  function subscribeToUserJobTitleChanged(
    cb: (
      payload: SignalRReceiveEvent<UserJobTitleChangedSignalRPayload>
    ) => void,
    options?: SubscribeToEventOptions
  ) {
    return subscribe<UserJobTitleChangedSignalRPayload>(
      signalREvent.AccessControl.V1.UserJobTitleChanged,
      cb,
      options
    )
  }

  function unsubscribeFromTenantUserEvents() {
    subscription.value?.()
    subscription.value = undefined
  }

  function subscribeToTenantUserEvents() {
    if (subscription.value) {
      return subscription.value
    }

    if (handlers) {
      const unsubscribeFunctions: (() => void)[] = []
      const addSubscription = createSubscriptionAdder(unsubscribeFunctions)

      addSubscription(
        handlers.onTenantUserCreated,
        subscribeToTenantUserCreated
      )
      addSubscription(
        handlers.onTenantUserNameChanged,
        subscribeToTenantUserNameChanged
      )
      addSubscription(
        handlers.onTenantUserPhoneNumberChanged,
        subscribeToTenantUserPhoneNumberChanged
      )
      addSubscription(
        handlers.onTenantUserProfileImageChanged,
        subscribeToTenantUserProfileImageChanged
      )
      addSubscription(
        handlers.onUserDeactivationScheduled,
        subscribeToUserDeactivationScheduled
      )
      addSubscription(handlers.onUserActivated, subscribeToUserActivated)
      addSubscription(
        handlers.onDatabaseAccessGranted,
        subscribeToDatabaseAccessGranted
      )
      addSubscription(
        handlers.onDatabaseAccessRevoked,
        subscribeToDatabaseAccessRevoked
      )
      addSubscription(
        handlers.onTenantUserDeactivated,
        subscribeToTenantUserDeactivated
      )
      addSubscription(
        handlers.onTenantUserActivated,
        subscribeToTenantUserActivated
      )
      addSubscription(
        handlers.onTenantAdminGranted,
        subscribeToTenantAdminGranted
      )
      addSubscription(
        handlers.onTenantAdminRevoked,
        subscribeToTenantAdminRevoked
      )
      addSubscription(
        handlers.onTenantUserEmailChanged,
        subscribeToTenantUserEmailChanged
      )
      addSubscription(
        handlers.onUserJobTitleChanged,
        subscribeToUserJobTitleChanged
      )

      subscription.value = () => {
        unsubscribeFunctions.forEach((unsubscribe) => unsubscribe())
      }
    }

    return subscription.value
  }
  return {
    unsubscribeFromTenantUserEvents,
    subscribeToTenantUserEvents
  }
}
